import React from 'react';
import SPAButton from '../shared/SPAButton';
import SPADialog from '../shared/SPADialog';
import SPAPill from '../shared/SPAPill';
import { GradeSyncLink } from './gradeSyncPath';

export interface BulkClass {
  id: number;
  name: string | null;
  students?: number;
  covered_students?: number;
  reason?: string;
}

export interface BulkPlan {
  assignment_id: number;
  title: string;
  classes: BulkClass[];
  staying?: BulkClass[];
  blocked?: string | null;
}

export interface BulkPayload {
  confirmed?: boolean;
  plans?: BulkPlan[];
  classes?: number;
  assignments?: number;
  blocked?: { reason: string; assignments: number }[];
  applied?: { assignment_id: number; removed?: number; refused?: string }[];
  error?: string;
}

// AIDEV-NOTE: One block per assignment, showing every class on it rather than only the ones coming
// off. A reviewer given a list of removals has to assume everything unnamed is fine, which is the
// assumption the coverage guard exists to stop anyone making.
const PlanBlock = ({ plan }: { plan: BulkPlan }) => (
  <div className="tw-border-0 tw-border-b tw-border-solid tw-border-spa-border tw-py-3">
    <GradeSyncLink
      className="tw-text-sm"
      to={`/assignments/${plan.assignment_id}`}
    >
      {plan.title}
      <span className="tw-ml-2 tw-font-mono tw-text-xs tw-text-spa-tertiary">
        {plan.assignment_id}
      </span>
    </GradeSyncLink>

    <ul className="tw-m-0 tw-mt-2 tw-list-none tw-p-0">
      {plan.classes.map(entry => (
        <li key={entry.id} className="tw-flex tw-items-baseline tw-gap-2 tw-py-0.5 tw-text-sm">
          <SPAPill tone="error">removing</SPAPill>
          <span className="tw-text-spa-primary">{entry.name ?? entry.id}</span>
          <span className="tw-text-sm tw-text-spa-tertiary">
            {`${entry.students ?? 0} students`}
            {(entry.covered_students ?? 0) > 0
              && ` · all ${entry.covered_students} with a score already have it in the LMS`}
          </span>
        </li>
      ))}
      {(plan.staying ?? []).map(entry => (
        <li key={entry.id} className="tw-flex tw-items-baseline tw-gap-2 tw-py-0.5 tw-text-sm">
          <SPAPill tone="neutral">keeping</SPAPill>
          <span className="tw-text-spa-secondary">{entry.name ?? entry.id}</span>
          <span className="tw-text-sm tw-text-spa-tertiary">{entry.reason}</span>
        </li>
      ))}
    </ul>
  </div>
);

interface BulkUnassignDialogProps {
  busy: boolean;
  open: boolean;
  plan?: BulkPayload;
  onCancel: () => void;
  onConfirm: () => void;
}

const BulkUnassignDialog = ({ busy, open, plan, onCancel, onConfirm }: BulkUnassignDialogProps) => {
  const plans = plan?.plans ?? [];
  const applied = plan?.confirmed === true;
  const removed = (plan?.applied ?? []).reduce((total, entry) => total + (entry.removed ?? 0), 0);

  return (
    <SPADialog open={open} onOpenChange={value => !value && onCancel()}>
      <SPADialog.Content size="lg">
        <SPADialog.Header>
          <SPADialog.Title>
            {applied ? 'Classes unassigned' : 'Review before unassigning'}
          </SPADialog.Title>
          <SPADialog.Description>
            This changes who these assignments are assigned to in TCI. Nothing in the LMS is created,
            renamed or deleted, and grades already delivered stay where they are.
          </SPADialog.Description>
        </SPADialog.Header>

        <SPADialog.Body>
          {applied && (
            <p className="tw-m-0 tw-text-sm tw-text-spa-primary">
              {`${removed} class assignments removed across ${(plan?.applied ?? []).length} assignments.`}
            </p>
          )}

          {!applied && plans.length === 0 && (
            <p className="tw-m-0 tw-text-sm tw-text-spa-primary">Nothing can be removed safely.</p>
          )}

          {!applied && plans.length > 0 && (
            <p className="tw-m-0 tw-mb-1 tw-text-sm tw-text-spa-secondary">
              {`${plan?.classes} class assignments would come off across ${plan?.assignments} `}
              assignments. Every one is covered: no student loses a route to a grade.
            </p>
          )}

          {!applied && plans.map(entry => <PlanBlock key={entry.assignment_id} plan={entry} />)}

          {/* The server's own words for each refusal — several guards can empty a plan, and
              summarising them into one sentence invents a reason. */}
          {!applied && (plan?.blocked ?? []).map(entry => (
            <p key={entry.reason} className="tw-m-0 tw-mt-2 tw-text-sm tw-text-spa-secondary">
              {`${entry.assignments} ${entry.assignments === 1 ? 'assignment' : 'assignments'} `}
              {`left alone: ${entry.reason}`}
            </p>
          ))}
        </SPADialog.Body>

        <SPADialog.Footer>
          <SPAButton type="button" variant="secondary" onClick={onCancel}>
            {applied ? 'Close' : 'Cancel'}
          </SPAButton>
          {!applied && plans.length > 0 && (
            <SPAButton disabled={busy} type="button" variant="primary" onClick={onConfirm}>
              {`Unassign across ${plan?.assignments} assignments`}
            </SPAButton>
          )}
        </SPADialog.Footer>
      </SPADialog.Content>
    </SPADialog>
  );
};

export default BulkUnassignDialog;
